import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DxTabsTypes } from 'devextreme-angular/ui/tabs';
import { ToastrService } from 'ngx-toastr';
import { SubscriptionTabsModel } from 'src/app/models/subscription-tabs.model';
import { MySubscriptionModel } from 'src/app/models/subscription/my-subscription.model';
import { TenantSubscriberModel } from 'src/app/models/subscription/tenant-subscriber.model';
import { TableColumn } from 'src/app/models/table-column.model';
import { LoadingService } from 'src/app/services/loading.service';
import { SubscriptionService } from 'src/app/services/subscriptions/subscription.service';
import { TenantContextService } from 'src/app/services/tenant-context.service';
import { TenantSubscriptionService } from 'src/app/services/tenants/tenant-subscription.service';
import { UserContextService } from 'src/app/services/user-context.service';

@Component({
  selector: 'app-subscriptions',
  standalone: false,
  templateUrl: './subscriptions.component.html',
  styleUrl: './subscriptions.component.scss'
})
export class SubscriptionsComponent implements OnInit {
  selectedTab: number = SubscriptionTabsModel.MySubscriptions;
  SubscriptionTabsModel = SubscriptionTabsModel;

  showRequestSubscriptionModal = false;
  showDisconnectSubscriptionModal = false;
  currentProviderTenantId: string | null = null;

  showRemoveSubscriberModal = false;
  currentConsumerTenantId: string | null = null;

  showSubscriptionSettingsModal = false;
  showRejectRequestModal = false;

  currentSubscriptionId: string | null = null;
  currentSubscriptionTitle: string | null = null;

  subscriptionIdToOpen: string | null = null;
  tenantSubscriptionIdToShow: string | null = null;
  publishToSubscribers: boolean = false;

  get canDisplayManageSubscribers(): boolean {
    return !this.isBroker && 
      this.publishToSubscribers && 
      !this.tenantSubscriptionIdToShow;
  }

  tabs: any[] = [
    {
      id: SubscriptionTabsModel.MySubscriptions,
      text: this.translateService.instant('SUBSCRIPTIONS.OVERVIEW.MY-SUBSCRIPTIONS'),
    },
    {
      id: SubscriptionTabsModel.ManageSubscribers,
      text: this.translateService.instant('SUBSCRIPTIONS.OVERVIEW.MANAGE-SUBSCRIBERS'),
    }
  ];
  
  mySubscriptionColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.COMPANY'),
      dataField: 'company',
      cssClass: 'width-100 height-75 text-center',
      cellTemplate: 'image', 
      disableFiltering: true,
      cellValue: (rowData: MySubscriptionModel) => {
        return rowData.company.fileUrl;
      }
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.DESCRIPTION'),
      dataField: 'description',
      cssClass: 'width-300',
      cellTemplate: 'titleDescription',
      disableFiltering: true,
      calculateDisplayValue: (rowData: MySubscriptionModel): any => {
        return {
          title: rowData.title ?? '',
          description: rowData.description
        };
      },
      filterExpression: (value: string) => {
        function getTitleDescriptionFilterString(rowData: MySubscriptionModel) {
          const text = `${rowData.title ?? ''} ${rowData.description ?? ''}`;
          return text.trim() || 'empty';
        }

        return [
          [
            getTitleDescriptionFilterString, 
            'contains', 
            value?.trim() || 'empty'
          ]
        ];
      },
      cellValue: (rowData: MySubscriptionModel): string => {
        return `${rowData.title} ${rowData.description}`
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.ACTIVE-SINCE'),
      dataField: 'activeSince',
      cssClass: 'width-150 text-center',
      cellValue: (data: MySubscriptionModel): string => {
        return this.datePipe.transform(data.activeSince, 'dd.MM.yyyy')!;
      }
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TRAININGS-AVAILABLE'),
      dataField: 'trainingsAvailable',
      cssClass: 'width-150 text-center',
    },
    // {
    //   caption: this.translateService.instant('COMMON.FIELDS.COURSES-AVAILABLE'),
    //   dataField: 'coursesAvailable',
    //   cssClass: 'width-150 text-center',
    // },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          text: this.translateService.instant('COMMON.SUBSCRIBE'),
          isShowAction: (data: MySubscriptionModel) => !data.isSubscribed && !data.isApprovalPending,
          isButton: true,
          buttonType: 'normal',
          onClick: (data: MySubscriptionModel) => {
            const createdByTenantId = 
              this.tenantContextService.getCreatedByTenantId();
             
            if(createdByTenantId && createdByTenantId == data.providerTenantId) {
              this.subscribeToProvidedTenant(createdByTenantId);
              return;
            }

            this.currentProviderTenantId = data.providerTenantId;
            this.showRequestSubscriptionModal = true;
          },
        },
        {
          text: this.translateService.instant('COMMON.APPROVAL-PENDING'),
          isShowAction: (data: MySubscriptionModel) => data.isApprovalPending,
          onClick: (_: MySubscriptionModel) => {},
        },
        {
          text: this.translateService.instant('COMMON.CANCEL-SUBSCRIPTION'),
          isShowAction: (data: MySubscriptionModel) => data.isSubscribed,
          isButton: true,
          buttonType: 'danger',
          onClick: (data: MySubscriptionModel) => {
            this.currentProviderTenantId = data.providerTenantId;
            this.showDisconnectSubscriptionModal = true;
          },
        },
        {
          text: this.translateService.instant('COMMON.SETTINGS'),
          isShowAction: (data: MySubscriptionModel) => data.isSubscribed,
          isButton: true,
          buttonType: 'default',
          onClick: (data: MySubscriptionModel) => {
            this.openSubscriptionSettings(data);
          },
        },
      ],
    },
  ];

  tenantSubscriberColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.COMPANY'),
      dataField: 'company',
      cssClass: 'width-100 height-75 text-center',
      cellTemplate: 'image', 
      disableFiltering: true,
      cellValue: (rowData: TenantSubscriberModel) => {
        return rowData.company?.fileUrl!;
      }
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.DESCRIPTION'),
      dataField: 'description',
      cssClass: 'width-300',
      cellTemplate: 'titleDescription',
      disableFiltering: true,
      calculateDisplayValue: (rowData: TenantSubscriberModel): any => {
        return {
          title: rowData.title ?? '',
          description: rowData.description
        };
      },
      filterExpression: (value: string) => {
        function getTitleDescriptionFilterString(rowData: TenantSubscriberModel) {
          const text = `${rowData.title ?? ''} ${rowData.description ?? ''}`;
          return text.trim() || 'empty';
        }

        return [
          [
            getTitleDescriptionFilterString, 
            'contains', 
            value?.trim() || 'empty'
          ]
        ];
      },
      cellValue: (rowData: TenantSubscriberModel): string => {
        return `${rowData.title} ${rowData.description}`
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.ACTIVE-SINCE'),
      dataField: 'activeSince',
      cssClass: 'width-150 text-center',
      cellValue: (data: TenantSubscriberModel): string => {
        return data.activeSince ? this.datePipe.transform(data.activeSince, 'dd.MM.yyyy')! : '';
      }
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TRAININGS-VISITED'),
      dataField: 'trainingsVisited',
      cssClass: 'width-150 text-center',
    },
    // {
    //   caption: this.translateService.instant('COMMON.FIELDS.COURSES-VISITED'),
    //   dataField: 'coursesVisited',
    //   cssClass: 'width-150 text-center',
    // },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          text: this.translateService.instant('COMMON.GRANT-APPROVAL'),
          isShowAction: (data: TenantSubscriberModel) => !data.isApproved,
          isButton: true,
          buttonType: 'normal',
          onClick: (data: TenantSubscriberModel) => {
            this.currentConsumerTenantId = data.consumerTenantId;
            this.grantApproval();
          },
        },
        {
          text: this.translateService.instant('COMMON.REJECT-REQUEST'),
          isShowAction: (data: TenantSubscriberModel) => !data.isApproved,
          isButton: true,
          buttonType: 'danger',
          onClick: (data: TenantSubscriberModel) => {
            this.currentSubscriptionId = data.subscriptionId;
            this.showRejectRequestModal = true;
          },
        },
        {
          text: this.translateService.instant('COMMON.REVOKE-APPROVAL'),
          isShowAction: (data: TenantSubscriberModel) => data.isApproved,
          isButton: true,
          buttonType: 'danger',
          onClick: (data: TenantSubscriberModel) => {
            this.currentConsumerTenantId = data.consumerTenantId;
            this.showRemoveSubscriberModal = true;
          },
        }
      ],
    },
  ];

  mySubscriptions: MySubscriptionModel[] = [];
  tenantSubscribers: TenantSubscriberModel[] = [];

  isBroker: boolean = false;

  constructor(
    private translateService: TranslateService,
    private loadingService: LoadingService,
    private subscriptionService: SubscriptionService,
    private datePipe: DatePipe,
    private toastrService: ToastrService,
    private route: ActivatedRoute,
    private userService: UserContextService,
    private tenantSubscriptionService: TenantSubscriptionService,
    private tenantContextService: TenantContextService,
    private toastService: ToastrService
  ) {
  }


  ngOnInit(): void {
    this.isBroker = this.userService.isBroker;

    this.route.queryParams.subscribe(params => {
      const tabKey = (params['tab'] || '').toLowerCase();

      switch (tabKey) {
        case 'subscriptions':
          this.selectedTab = SubscriptionTabsModel.MySubscriptions;
          break;

        case 'subscribers':
          this.selectedTab = SubscriptionTabsModel.ManageSubscribers;
          break;

        default:
          this.selectedTab = SubscriptionTabsModel.MySubscriptions;
          break;
      }

      this.subscriptionIdToOpen = params['open-settings'] === 'true'
        ? params['subscriptionId']
        : null;

      this.tenantSubscriptionIdToShow = params['open-settings'] === 'true'
        ? null
        : params['tenantSubscriptionId'] || null;

      this.loadingService.load(
        this.tenantSubscriptionService.getCurrentTenantSubscription()
      ).subscribe(subscription => {
        this.publishToSubscribers = subscription?.publishToSubscribers ?? false;

        if(
          !this.canDisplayManageSubscribers && 
          this.selectedTab == SubscriptionTabsModel.ManageSubscribers
        ) {
          this.selectedTab = SubscriptionTabsModel.MySubscriptions;
        }
      });

      this.loadSubscriptions();
    });
  }

  loadSubscriptions(): void {
    if (this.tenantSubscriptionIdToShow) {
      this.loadSubscription(this.tenantSubscriptionIdToShow);
      return;
    }

    this.loadingService.load(
      this.subscriptionService.getSubscriptionsTemplateSummary()
    ).subscribe((subscriptionsTemplateSummary) => {
      this.mySubscriptions = 
        this.prepareTableData(subscriptionsTemplateSummary.mySubscriptions);

      this.tenantSubscribers = this.canDisplayManageSubscribers 
        ? this.prepareTableData(subscriptionsTemplateSummary.tenantSubscribers)
        : [];

      if (this.subscriptionIdToOpen) {
        const subscription = this.mySubscriptions.find(
          s => s.subscriptionId === this.subscriptionIdToOpen
        );

        if (subscription) {
          this.openSubscriptionSettings(subscription);
        }

        this.subscriptionIdToOpen = null;
      }
    });
  }

   loadSubscription(tenantSubscriptionId: string): void {
    this.loadingService.load(
      this.subscriptionService.getMySubscription(tenantSubscriptionId)
    ).subscribe((subscription) => {
      if(subscription) {
        this.mySubscriptions = 
          this.prepareTableData([subscription]);
      }
    });
  }

  private openSubscriptionSettings(subscription: MySubscriptionModel): void {
    this.currentSubscriptionId = subscription.subscriptionId;
    this.currentSubscriptionTitle = subscription.title;
    this.showSubscriptionSettingsModal = true;

    this.subscriptionService
      .markProviderCategoriesAsReviewed(subscription.subscriptionId)
      .subscribe();
  }

  onTabChange(e: DxTabsTypes.ItemClickEvent) {
    this.selectedTab = e.itemData.id;
  }

  subscribeToProvidedTenant(tenantId: string) {
    this.loadingService.load(
      this.subscriptionService.subscribeToProvidedTenant(
        tenantId, []
      )
    ).subscribe(() => {
      this.toastService.success(
        this.translateService.instant(
          'SUBSCRIPTIONS.SUBSCRIBED-PROVIDED-TENANT-SUCCESSFULLY'
        )
      );
      this.loadSubscriptions();
    });
  }

  onRequestSubscription() {
    this.currentProviderTenantId = null;
    this.loadSubscriptions();
  }

  onRemoveSubscriber() {
    this.currentConsumerTenantId = null;
    this.loadSubscriptions();
  }

  onSaveSubscriptionSettings() {
    this.currentSubscriptionId = null;
    this.loadSubscriptions();
  }

  onDisconnectSubscription() {
    this.currentProviderTenantId = null;
    this.loadSubscriptions();
  }

  grantApproval() {
    this.loadingService.load(
      this.subscriptionService.grantApproval(this.currentConsumerTenantId!)
    ).subscribe(() => {
      this.toastrService.success(
        this.translateService.instant('SUBSCRIPTIONS.GRANTED-APPROVAL-SUCCESSFULLY')
      );

      this.currentConsumerTenantId = null;
      this.loadSubscriptions();
    });
  }

  onRejectRequest() {
    this.currentSubscriptionId = null;
    this.loadSubscriptions();
  }

  private prepareTableData(data: any[]): any[] {
    return data.map((item, index) => {
      return {
        id: index,
        ...item
      };
    });
  }
}
